'use strict';

import {
	Box3,
	FileLoader,
	Group,
	Object3D,
	Vector3
} from 'three';
import {
	assignDeep, Buildings,
	GSAPAnimationMixer, isDesktop, isIOS,
	isMobile, isTablet, isWeb,
	resourcesFolder,
	settingsData,
	threeJsVariables,
} from '../../settings';
import { TilesPanoramaViewer } from '../components/TilesPanoramaViewer/TilesPanoramaViewer';
import { SchematicBuilding } from '../threejs/SchematicBuilding/SchematicBuilding';
import { getBuildingShape } from '../crm/crm-requests';
import { getUrlParam, userPrevent } from '../../functions';
import { Building } from '../building/Building';
import { concatPropsAndHighlights, setHighlights } from '../threejs/autoSelectionBoxes';
import { createTooltip } from '../components/flatTooltip/flatTooltip';
import gsap from "gsap";
import { Lerp } from '../projectTime/Lerp';
import { Environment } from '../components/Environment/Environment';
import { BaseElement } from '../interfaces/BaseElement';
import { CSS2DObject } from '../../libs/CSS2DRenderer';
import { Cloudflare } from '../api/Cloudflare';

const DEFAULT_PARAMS = {
	active: false,
	defaultPanoramaId: '',
	useSchematicBuildings: false,
	simplifyBundle: false,
	sceneObjects: [],
	devices: [],
	plugins: ["BuildingsPlugin","BuildingsHomeLabelPlugin","PanoramaToMapPlugin","PanoramaCompassPlugin","BuildingLabelsPlugin"],
	panoramas: {}
};

export class AirMode extends Group{
	
	static blockMainControlsUpdate = false;
	
	constructor () {
		super();
		
		this.params = DEFAULT_PARAMS;
		this.panoramas = [];
		this.labels = [];
		this.activePanorama = null;
		this.isConfigLoaded = false;
		this.backup = {
			effects: new Map(),
			controls: new Map()
		};
		this.name = 'AirMode';
		this.alpha = 1;
		this.#getConfig();
	}
	
	#getConfig() {
		const fileLoader = new FileLoader();
		fileLoader.load(`${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/textures/air/airModeConfig.json?v=${Cloudflare.version}`,(cfg) => {
			this.isConfigLoaded = true;
			this.params = assignDeep(this.params,JSON.parse(cfg));
			this.params.active = this.isDeviceSupported();
			
			const lowPerformance = localStorage.getItem('lowPerformance');
			if (lowPerformance && Object.keys(this.params.panoramas).length) {
				localStorage.removeItem('lowPerformance');
				this.params.active = true;
				if (config.features.dayNight) {
					config.features.dayNight.checked = true;
				}
			}
			
			if (this.params.active) {
				if (Object.keys(this.params.panoramas).length === 0) {
					this.params.active = false;
				}
			}
		});
	}
	
	isDeviceSupported() {
		if (!this.active)
			return false;
		
		if (this.params.devices.length === 0)
			return true;
		
		let supported = true;
		let device = 'none';
		if (isMobile) {
			if (isIOS) {
				device = 'Iphone';
			} else {
				device = 'Android';
			}
		} else if (isWeb) {
			device = 'Web';
		} else if (isDesktop) {
			device = 'Desktop';
		} else if (isTablet) {
			device = 'Tablet';
		}

		if (!this.params.devices.includes(device)) {
			supported = false;
		}
		
		return supported;
	}
	
	init(textureLoader) {
		if (!this.active)
			return;
		
		threeJsVariables.scene.add(this);
		threeJsVariables.composer.disableAll();
		
		Object.entries(this.params.panoramas).forEach(([id,cfg]) => {
			if (cfg.plugins.length === 0) {
				cfg.plugins = this.params.plugins;
			}
			const panorama = new TilesPanoramaViewer(threeJsVariables.perspectiveCamera,threeJsVariables.renderer,cfg);
			panorama.name += `_${id}`;
			panorama.panoramaId = id;
			panorama.init(textureLoader);
			this.add(panorama);
			this.panoramas.push(panorama);
			
			if (id === this.params.defaultPanoramaId) {
				this.activePanorama = panorama;
			}
		});
		
		if (!this.activePanorama) {
			this.activePanorama = this.panoramas[0];
		}
		
		if (this.panoramas.length > 1) {
			this.#createPanoramasPoints();
		}
	}
	
	toggleSceneObjects(state) {
		if (!this.active)
			return;
		
		this.params.sceneObjects.forEach(objName => {
			const obj = threeJsVariables.scene.getObjectByName(objName);
			if (obj) {
				obj.visible = state;
			}
		});
	}
	
	#createPanoramasPoints() {
		const labelsGroup = new Group();
		labelsGroup.name = 'Labels';
		this.add(labelsGroup);
		
		this.panoramas.forEach(panorama => {
			const element = new BaseElement()
				.addClass('air-mode__panorama-point')
				.event('click',() => this.#portalClickHandler(panorama.panoramaId))
				.DOMElement;
			
			new BaseElement()
				.addClass('air-mode__panorama-point__icon','icon-360')
				.appendTo(element);
			
			const label = new CSS2DObject(element);
			label.name = `Panorama-label_${panorama.panoramaId}`;
			label.userData.panoramaId = panorama.panoramaId;
			label.userData.zoom = 1;
			label.position.copy(panorama.position);
			
			if (this.activePanorama.panoramaId === panorama.panoramaId) {
				label.visible = false;
			}
			this.labels.push(label);
			labelsGroup.add(label);
		});
	}
	
	#portalClickHandler(clickedId) {
		const activePanoramaLabel = this.labels.find(label => label.userData.panoramaId === this.activePanorama.panoramaId);
		const targetPanorama = this.panoramas.find(pano => pano.panoramaId === clickedId);
		const targetLabel = this.labels.find(label => label.userData.panoramaId === clickedId);
		
		const mixerPass = threeJsVariables.composer.getPass('mixerPass');
		mixerPass.active = true;
		
		const progress = {
			value: 0
		};
		
		Environment.hide();
		
		gsap.to(progress,{
			value: 1,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease,
			onStart: () => setTimeout(() => {
				targetLabel.visible = false;
				this.activePanorama.disable();
				this.activePanorama.visible = false;
				
				this.activePanorama = targetPanorama;
				this.activePanorama.visible = true;
				if (!this.activePanorama.started) {
					this.activePanorama.start();
				} else {
					this.activePanorama.enable();
				}
				this.activePanorama.alpha = 1 - this.alpha;
			},0),
			onUpdate: () => {
				mixerPass.mix(progress.value);
				mixerPass.setTransitionScale(1 - progress.value);
			},
			onComplete: () => {
				mixerPass.active = false;
				activePanoramaLabel.visible = true;
				this.activePanorama.enablePlugins();
				this.activePanorama.updateTiles();
				Environment.show();
			}
		});
		
	}
	
	prepareBundleForAirMode(bundleConfig) {
		if (!this.active || !this.params.simplifyBundle)
			return;
		
		bundleConfig.instances = Object.entries(bundleConfig.instances).reduce((acc,[key,obj]) => {
			if (key.toLowerCase().match(/onemesh|interior/g) || this.params.sceneObjects.includes(key)) {
				acc[key] = obj;
			}
			return acc;
		},{});
		bundleConfig.decals = {};
		bundleConfig.sceneObjects = Object.entries(bundleConfig.sceneObjects).reduce((acc,[key,obj]) => {
			if (key.toLowerCase().match(/onemesh|interior/g) || this.params.sceneObjects.includes(key)) {
				acc[key] = obj;
			}
			return acc;
		},{});
		
		Object.entries(bundleConfig.effects).forEach(([name,cfg]) => {
			this.backup.effects.set(name,JSON.parse(JSON.stringify(cfg)));
			if (cfg.hasOwnProperty('active')) {
				cfg.active = false;
			} else {
				cfg.enabled = false;
			}
		});
	}
	
	saveState() {
		if (!this.active)
			return;
		
		const orbitControls = threeJsVariables.orbitControls;
		const camera = threeJsVariables.perspectiveCamera;
		
		this.backup.controls.set('position',camera.position.clone());
		this.backup.controls.set('fov',camera.fov);
		this.backup.controls.set('target',orbitControls.target.clone());
	}
	
	restoreState() {
		if (!this.active)
			return;
		
		const orbitControls = threeJsVariables.orbitControls;
		const camera = threeJsVariables.perspectiveCamera;
		
		camera.fov = this.backup.controls.get('fov');
		camera.updateProjectionMatrix();
		camera.position.copy(this.backup.controls.get('position'));
		orbitControls.target.copy(this.backup.controls.get('target'));
		
		this.backup.effects.forEach((value,key) => {
			const pass = threeJsVariables.composer.getPass(key);
			pass.config = value;
		});
	}
	
	start() {
		if (!this.active)
			return;
		
		this.panoramas.forEach(panorama => {
			if (this.activePanorama !== panorama) {
				panorama.visible = false;
			}
		});
		
		this.setMainControlsState(false);
		AirMode.blockMainControlsUpdate = true;
		
		
		if (Buildings.oneMeshModel) {
			Buildings.oneMeshModel.visible = false;
		}
		
		this.toggleSceneObjects(false);
		this.activePanorama?.start();
	}
	
	setMainControlsState(targetState) {
		threeJsVariables.orbitControls.enabled = targetState;
		threeJsVariables.trackballControls.enabled = targetState;
	}
	
	update() {
		if (!this.active)
			return;
		
		this.activePanorama?.updateTiles();
	}
	
	project(data) {
		if (!this.active || this.activePanorama === null)
			return data;
		
		return this.activePanorama.project(data);
	}
	
	resize(w,h) {
		if (!this.active)
			return;
		
		for (let i = 0; i < this.panoramas.length; i++) {
			this.panoramas[i].resize(w,h);
		}
	}
	
	cylinderEnterAnimation(mixer) {
		if (!this.active)
			return;
		
		const mixerPass = threeJsVariables.composer.getPass('mixerPass');
		mixerPass.active = true;
		
		const progress = {
			value: 0
		};
		
		const animation = gsap.to(progress,{
			value: 1,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease,
			onStart: () => setTimeout(() => this.hide(),0),
			onUpdate: () => {
				mixerPass.mix(progress.value);
				mixerPass.setTransitionScale(1 - progress.value);
			},
			onComplete: () => mixerPass.active = false
		});
		
		mixer.addAnimation(animation,0);
	}
	
	cylinderExitAnimation(mixer) {
		if (!this.active)
			return;
		
		this.show();
		
		const transition = threeJsVariables.composer.getPass('map');
		transition.scale = 0;
		transition.opacity = 1;
		transition.active = true;
		const labelPlugin = this.activePanorama?.getPlugin('BuildingLabelsPlugin');

		const progress = {
			value: 0
		};

		const animation = gsap.to(progress,{
			value: 1,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease,
			onStart: () => {
				this.labels.forEach(label => label.visible = false);
				labelPlugin && labelPlugin.disable();
			},
			onUpdate: () => {
				transition.opacity = progress.value;
			},
			onComplete: () => {
				transition.active = false;
				labelPlugin && labelPlugin.enable();
				this.labels.forEach(label => label.visible = label.userData.panoramaId !== this.activePanorama.panoramaId);
			}
		});
		
		mixer.addAnimation(animation,0);
	}
	
	orbitToMapAnimation() {
		if (!this.active)
			return new Promise(resolve => resolve());
		
		return new Promise(resolve => {
			const transition = threeJsVariables.composer.getPass('map');
			transition.scale = 0;
			transition.opacity = 1;
			transition.active = true;
			
			userPrevent(true);
			
			const opacityLerp = new Lerp(1,2);
			const scaleLerp = new Lerp(0,1);
			const labelPlugin = this.activePanorama?.getPlugin('BuildingLabelsPlugin');
			
			const progress = {
				value: 0
			};
			
			gsap.to(progress,{
				value: 1,
				duration: GSAPAnimationMixer.duration,
				ease: GSAPAnimationMixer.ease,
				onStart: () => {
					labelPlugin && labelPlugin.disable();
					Environment.hide();
					this.labels.forEach(label => label.visible = false);
					if (this.activePanorama) {
						this.activePanorama.disable();
					}
				},
				onUpdate: () => {
					transition.opacity = opacityLerp.lerp(progress.value);
					transition.scale = scaleLerp.lerp(progress.value);
					if (progress.value > 0.9) {
						userPrevent(false);
						resolve();
					}
				},
				onComplete: () => {
					transition.active = false;
					labelPlugin && labelPlugin.enable();
					Environment.show();
					this.labels.forEach(label => {
						label.visible = label.userData.panoramaId !== this.activePanorama.panoramaId;
					});
					if (this.activePanorama) {
						this.activePanorama.enablePlugins();
						this.activePanorama.enable();
					}
				}
			});
		});
	}
	
	mapToOrbitAnimation() {
		if (!this.active)
			return new Promise(resolve => resolve());
		
		return new Promise(resolve => {
			const transition = threeJsVariables.composer.getPass('map');
			transition.scale = 1;
			transition.opacity = 2;
			transition.active = true;
			
			userPrevent(true);
			
			const opacityLerp = new Lerp(2,1);
			const scaleLerp = new Lerp(1,0);
			const labelPlugin = this.activePanorama?.getPlugin('BuildingLabelsPlugin');
			
			const progress = {
				value: 0
			};
			
			gsap.to(progress,{
				value: 1,
				duration: GSAPAnimationMixer.duration,
				ease: GSAPAnimationMixer.ease,
				onStart: () => {
					labelPlugin && labelPlugin.disable();
					Environment.hide();
					this.labels.forEach(label => label.visible = false);
					if (this.activePanorama) {
						this.activePanorama.disable();
					}
				},
				onUpdate: () => {
					transition.opacity = opacityLerp.lerp(progress.value);
					transition.scale = scaleLerp.lerp(progress.value);
				},
				onComplete: () => {
					transition.active = false;
					labelPlugin && labelPlugin.enable();
					Environment.show();
					this.labels.forEach(label => {
						label.visible = label.userData.panoramaId !== this.activePanorama.panoramaId;
					});
					if (this.activePanorama) {
						this.activePanorama.enablePlugins();
						this.activePanorama.enable();
					}
					userPrevent(false);
					resolve();
				}
			});
		});
	}
	
	hide() {
		if (!this.active)
			return;
		
		if (this.activePanorama) {
			this.activePanorama.disable();
		}
		
		AirMode.blockMainControlsUpdate = false;
		
		this.labels.forEach(label => label.visible = false);
		
		this.visible = false;
		this.restoreState();
		this.setMainControlsState(true);
	}
	
	show() {
		if (!this.active)
			return;
		
		AirMode.blockMainControlsUpdate = true;
		
		this.setMainControlsState(false);
		
		if (this.activePanorama) {
			this.activePanorama.enable();
			this.activePanorama.enablePlugins();
		}
		
		this.labels.forEach(label => label.visible = label.userData.panoramaId !== this.activePanorama.panoramaId);
		
		threeJsVariables.composer.disableAll();
		this.visible = true;
	}
	
	async createBuildings() {
		if (!this.active)
			return;
		
		threeJsVariables.slicer.params.distance = 500000;
		threeJsVariables.slicer.setSlicerPresets({});
		threeJsVariables.slicer.setDefaultPlanes();
		threeJsVariables.sceneObjectsSlicer.params.distance = 500000;
		threeJsVariables.sceneObjectsSlicer.setSlicerPresets({});
		threeJsVariables.sceneObjectsSlicer.setDefaultPlanes();
		
		threeJsVariables.schematicBuilding = new SchematicBuilding(settingsData.allCrmData);
		const type = config.features.liteMode.buildingType;
		
		if (type) {
			if (Array.isArray(type)) {
				threeJsVariables.schematicBuilding.type = 'custom';
				threeJsVariables.schematicBuilding.setShape(type);
			} else {
				threeJsVariables.schematicBuilding.type = type;
			}
		} else {
			const response = await getBuildingShape();
			
			if (response && response.data) {
				const settings = response.data;
				threeJsVariables.schematicBuilding.type = settings.type;
				if (threeJsVariables.schematicBuilding.type === 'custom') {
					threeJsVariables.schematicBuilding.setShape(settings.shape);
				}
				
				if (settings.advanced) {
					threeJsVariables.schematicBuilding.advancedSettings = settings.advanced;
				}
			}
		}
		
		if (getUrlParam('mode') === 'tools') {
			threeJsVariables.schematicBuilding.createUI();
		}
		threeJsVariables.schematicBuilding.create();
		const selections = [];
		
		Object.entries(threeJsVariables.schematicBuilding.buildings).forEach(([name,data]) => {
			selections.push(data.selectionsGroup);
			const model = data.buildingGroup.children[0];
			model.layers.set(0);
			const building = new Building(model);
			const modelGroup = building.create();
			modelGroup.visible = false;
			data.buildingGroup.removeFromParent();
			threeJsVariables.schematicBuilding.buildingGroup.add(modelGroup);
			building.amenities.Roof = new Object3D();
			threeJsVariables.slicer.add(model);
		});
		
		concatPropsAndHighlights({data:settingsData.allCrmData},selections,{data: config.crmProjectSettings});
		threeJsVariables.scene.add(threeJsVariables.schematicBuilding.buildingGroup);
		
		setHighlights(threeJsVariables.schematicBuilding.selectionsGroup);
		if (!isMobile) createTooltip();
	}
	
	updateAlpha(alpha) {
		if (!this.active)
			return;
		
		this.alpha = alpha;
		
		for (let i = 0; i < this.panoramas.length; i++) {
			this.panoramas[i].alpha = 1 - this.alpha;
			this.panoramas[i].updateAlpha();
		}
	}
	
	isSchematicBuildings() {
		if (this.active) {
			return this.params.useSchematicBuildings;
		}
		return false;
	}
	
	getCurrentTargetDirection() {
		if (this.activePanorama) {
			const camPosition = threeJsVariables.perspectiveCamera.position.clone();
			const panoPosition = this.activePanorama.controls.target.clone();
			
			return camPosition.sub(panoPosition);
		}
	}
	
	isVisible() {
		if (!this.active)
			return false;
		
		return this.visible;
	}
	
	unblockMainControls() {
		this.activePanorama.disable();
		AirMode.blockMainControlsUpdate = false;
		this.setMainControlsState(true);
		// threeJsVariables.trackballControls.maxDistance = Infinity;
	}
	
	raycastPanorama() {
		threeJsVariables.raycaster.setFromCamera(threeJsVariables.mouse, threeJsVariables.perspectiveCamera);
		const intersects = threeJsVariables.raycaster.intersectObject(this, true);
		if (intersects.length) {
			console.log(intersects[0].point.toArray());
		}
	}
	
	hasSkyMask() {
		if (!this.active)
			return false;
		
		let value = false;
		
		if (Object.keys(this.params.panoramas).length === 0) {
			value = false;
		} else {
			value = Object.values(this.params.panoramas).some(pano => pano.masks.hasOwnProperty('sky'));
		}
		
		return value;
	}
	
	hasDayAndNight() {
		if (!this.active)
			return false;
		
		let value = false;
		
		if (Object.keys(this.params.panoramas).length === 0) {
			value = false;
		} else {
			value = Object.values(this.params.panoramas).some(pano => pano.nightTilesUrl && pano.nightTilesUrl.length > 0);
		}
		
		return value;
	}
	
	getBbox() {
		const box = new Box3();
		box.setFromObject(this.activePanorama,true);
		const size = new Vector3();
		box.getSize(size);
		console.log(size);
	}
	
	get active() {
		return this.params.active;
	}
	
}
